import { ItineraryItem } from '../../types/Quotation';

export type ItineraryPageEntry = {
  key: string;
  day: ItineraryItem;
};

export type ItineraryPaginationOptions = {
  availableHeight?: number;
  firstPageTitleHeight?: number;
  continuationTitleHeight?: number;
  imageHeights?: number[];
  itemGap?: number;
  itemHeights?: Record<string, number>;
  defaultItemHeight?: number;
};

export type ItineraryPage = {
  items: ItineraryPageEntry[];
  includeImages: boolean;
};

export const DEFAULT_ITINERARY_PAGINATION: Required<ItineraryPaginationOptions> = {
  availableHeight: 860,
  firstPageTitleHeight: 72,
  continuationTitleHeight: 72,
  imageHeights: [],
  itemGap: 24,
  itemHeights: {},
  defaultItemHeight: 360,
};

export const buildItineraryPageEntries = (itinerary: ItineraryItem[]): ItineraryPageEntry[] =>
  itinerary.map((day, index) => ({
    key: `day-${day.day}-${index}`,
    day,
  }));

export const paginateItineraryEntries = (
  items: ItineraryPageEntry[],
  options: ItineraryPaginationOptions = {}
): ItineraryPage[] => {
  const {
    availableHeight,
    firstPageTitleHeight,
    continuationTitleHeight,
    imageHeights,
    itemGap,
    itemHeights,
    defaultItemHeight,
  } = {
    ...DEFAULT_ITINERARY_PAGINATION,
    ...options,
  };

  if (items.length === 0) {
    return imageHeights.length > 0 ? [{ items: [], includeImages: true }] : [];
  }

  const pages: ItineraryPage[] = [];
  let index = 0;
  let isFirstPage = true;

  while (index < items.length || (isFirstPage && imageHeights.length > 0 && pages.length === 0)) {
    const includeImages = isFirstPage && imageHeights.length > 0;
    const reservedHeight =
      (isFirstPage ? firstPageTitleHeight : continuationTitleHeight) +
      (includeImages ? imageHeights.reduce((sum, height) => sum + height, 0) : 0);
    const pageItems: ItineraryPageEntry[] = [];
    let usedHeight = reservedHeight;

    while (index < items.length) {
      const item = items[index];
      const itemHeight = itemHeights[item.key] || defaultItemHeight;
      const nextHeight = itemHeight + (pageItems.length > 0 ? itemGap : 0);

      if (pageItems.length > 0 && usedHeight + nextHeight > availableHeight) {
        break;
      }

      if (pageItems.length === 0 && usedHeight + nextHeight > availableHeight) {
        if (includeImages) {
          break;
        }

        pageItems.push(item);
        index += 1;
        break;
      }

      pageItems.push(item);
      usedHeight += nextHeight;
      index += 1;
    }

    if (pageItems.length === 0 && !includeImages) {
      pageItems.push(items[index]);
      index += 1;
    }

    pages.push({
      items: pageItems,
      includeImages,
    });
    isFirstPage = false;
  }

  return pages;
};
